import type { FieldUnitSpec, Measure } from "lab-testing-units";
import { action, makeObservable, observable } from "mobx";
import {
  asDisplayUnits,
  type EquipmentUsedEntry,
  type LaboratoryInfo,
  type LabTestMetadata,
  type ProjectInfo,
  type SampleInfo,
  type StateTransitionContract,
  type TestRelationshipInput,
} from "@/contracts";
import { DisplayUnits } from "@/models/displayUnits/DisplayUnits";
import type { LabTestStatus } from "../LabTestStatus";

export class LabTestDataModel {
  readonly id: string;
  readonly type: string;
  readonly method: string;
  readonly createdAt: string;

  private readonly unitChoices: DisplayUnits;

  status: LabTestStatus;
  stateTransition: StateTransitionContract;

  processingTestId?: string | null;
  laboratory: LaboratoryInfo;
  sample?: SampleInfo;
  project?: ProjectInfo;

  constructor(data: LabTestMetadata) {
    this.unitChoices = new DisplayUnits(asDisplayUnits(data.displayUnits));
    this.id = data.id;
    this.type = data.type;
    this.method = data.method;
    this.createdAt = data.createdAt;

    this.status = data.status;
    this.stateTransition = data.stateTransition ?? {};
    this.processingTestId = data.processingTestId;
    this.laboratory = data.laboratory;
    this.sample = data.sample;
    this.project = data.project;

    makeObservable(this, {
      status: observable,
      stateTransition: observable,
      laboratory: observable,
      sample: observable,
      project: observable,
      setStatus: action,
    });
  }

  setStatus(status: LabTestStatus) {
    this.status = status;
  }

  unitFor(key: string, spec: FieldUnitSpec, entered?: Measure | null): string {
    return this.unitChoices.unitFor(key, spec, entered);
  }

  setUnit(key: string, unitId: string): void {
    this.unitChoices.setUnit(key, unitId);
  }

  dispose(): void {
    // Override in derived classes
  }

  getCompletionMetadata(): object | undefined {
    return undefined;
  }

  getCompletionRelationships(): TestRelationshipInput[] | undefined {
    return undefined;
  }

  getEquipmentUsed(): EquipmentUsedEntry[] {
    return [];
  }

  toJSON(): LabTestMetadata {
    // getEquipmentUsed runs here on partial data via the auto-save reaction; a throwing override must not break saving.
    let equipmentUsed: EquipmentUsedEntry[] = [];
    try {
      equipmentUsed = this.getEquipmentUsed();
    } catch {
      equipmentUsed = [];
    }
    return {
      id: this.id,
      type: this.type,
      method: this.method,
      createdAt: this.createdAt,
      status: this.status,
      stateTransition: this.stateTransition,
      processingTestId: this.processingTestId,
      laboratory: this.laboratory,
      sample: this.sample,
      project: this.project,
      equipmentUsed,
      displayUnits: this.unitChoices.toJSON(),
    };
  }
}
